'use client';

import React, { useState, useEffect, useRef } from 'react';

/**
 * LazySection
 * Mounts children only when the section is within `rootMargin` (default: 400px) of viewport.
 * Maintains minHeight placeholder before mounting to prevent Cumulative Layout Shift (CLS).
 */
export default function LazySection({ children, minHeight = 360, placeholder = null }) {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (isVisible) return;

        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry && entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                rootMargin: '400px 0px',
                threshold: 0,
            }
        );

        const currentEl = containerRef.current;
        if (currentEl) {
            observer.observe(currentEl);
        }

        return () => {
            if (currentEl) {
                observer.unobserve(currentEl);
            }
            observer.disconnect();
        };
    }, [isVisible]);

    return (
        <div
            ref={containerRef}
            style={{ minHeight: isVisible ? undefined : `${minHeight}px` }}
            className="w-full"
        >
            {isVisible ? children : (placeholder || <div style={{ height: `${minHeight}px` }} />)}
        </div>
    );
}
